import { CameraCCTVModel, CameraModel, IDocumentFileModel, ProjectModel, UserModel } from 'api/models';
import { Avatar } from 'library/avatar';
import Img from 'library/media/components/Img';
import { OverflowTooltip } from 'library/tooltip';
import { getProjectFirstAsset } from 'modules/dashboard/utils/project-utils';
import DocFileIcon from 'modules/shared/components/helpers/DocFileIcon';
import { getFileExtension } from 'modules/shared/utils/file-utils';
import { FC } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { SearchTypeEnum } from '../../enums/searchbar.enums';
// TODO Documents -- pc_docs
/******************************************* ProjectSearchRow *******************************************/

interface ProjectSearchResultRowProps {
    data: ProjectModel;
    onResultClick: () => void;
}
const ProjectSearchResultRow: FC<ProjectSearchResultRowProps> = (props) => {
    const { data: project, onResultClick } = props;

    const navigate = useNavigate();

    const handleClick = () => {
        if (project.hasCameras) {
            const asset = getProjectFirstAsset(project.assets);
            const to = asset?.to || 'cameras';

            navigate(`/projects/${project._id}/${to}`);
        } else {
            navigate(`/projects/${project._id}`);
        }

        onResultClick();
    };

    return (
        <div className="floating-list-item py-[10px]" onClick={handleClick}>
            <div>
                <Img
                    parentClassName="w-full h-9"
                    className="w-full h-9 aspect-square rounded-md object-cover object-center"
                    src={project.coverImageUrl}
                />
            </div>
            <OverflowTooltip className="text-sm font-medium text-grayscale-600">{project.name}</OverflowTooltip>
        </div>
    );
};

/******************************************* CameraSearchRow *******************************************/

interface CameraSearchResultRowProps {
    data: CameraModel;
    onResultClick: () => void;
}
const CameraSearchResultRow: FC<CameraSearchResultRowProps> = (props) => {
    const { data: camera, onResultClick } = props;

    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/projects/${(camera.project as ProjectModel)._id}/cameras/${camera._id}`);

        onResultClick();
    };

    return (
        <div className="floating-list-item py-[10px]" onClick={handleClick}>
            <div>
                <Img
                    parentClassName="w-full h-9"
                    className="w-full h-9 aspect-square rounded-md object-cover object-center"
                    src={camera.latestImage?.urlThumb}
                />
            </div>
            <div className="flex items-center gap-1 overflow-hidden">
                <OverflowTooltip className="text-sm font-medium text-grayscale-600">{camera.name}</OverflowTooltip>
                <div className="text-xs font-medium text-grayscale-400">/</div>
                <OverflowTooltip className="text-xs font-medium text-grayscale-400">
                    {(camera.project as ProjectModel).name}
                </OverflowTooltip>
            </div>
        </div>
    );
};

/******************************************* CameraSearchRow *******************************************/

interface CameraCctvSearchResultRowProps {
    data: CameraCCTVModel;
    onResultClick: () => void;
}
const CameraCctvSearchResultRow: FC<CameraCctvSearchResultRowProps> = (props) => {
    const { data: camera, onResultClick } = props;

    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/projects/${(camera.project as ProjectModel)._id}/live-cameras/${camera._id}`);

        onResultClick();
    };

    return (
        <div className="floating-list-item py-[10px]" onClick={handleClick}>
            <div>
                <Img
                    parentClassName="w-full h-9"
                    className="w-full h-9 aspect-square rounded-md object-cover object-center"
                    src={camera.defaultChannel.latestImage?.url}
                />
            </div>
            <div className="flex items-center gap-1 overflow-hidden">
                <OverflowTooltip className="text-sm font-medium text-grayscale-600">{camera.name}</OverflowTooltip>
                <div className="text-xs font-medium text-grayscale-400">/</div>
                <OverflowTooltip className="text-xs font-medium text-grayscale-400">
                    {(camera.project as ProjectModel).name}
                </OverflowTooltip>
            </div>
        </div>
    );
};

/******************************************* UserSearchResultRow *******************************************/

interface UserSearchResultRowProps {
    data: UserModel;
    onResultClick: () => void;
}
const UserSearchResultRow: FC<UserSearchResultRowProps> = (props) => {
    const { data: user, onResultClick } = props;

    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/users/${user._id}`);

        onResultClick();
    };

    return (
        <div className="floating-list-item py-[10px]" onClick={handleClick}>
            <div>
                <Avatar src={user.dpUrl} alt={user.name} backgroundColor={user.preset.color} />
            </div>

            <div className="overflow-hidden">
                <OverflowTooltip className="text-sm font-medium text-grayscale-600">{user.name}</OverflowTooltip>

                {user.designation ? (
                    <OverflowTooltip className="text-xs font-medium text-grayscale-400">
                        {user.designation}
                    </OverflowTooltip>
                ) : (
                    <i>No designation</i>
                )}
            </div>
        </div>
    );
};

/******************************************* DocumentSearchResultRow *******************************************/

interface FilesSearchResultRowProps {
    data: IDocumentFileModel;
    onResultClick: () => void;
}
const FilesSearchResultRow: FC<FilesSearchResultRowProps> = (props) => {
    const { data: file, onResultClick } = props;
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(
            {
                pathname: `/documents`,
                search: createSearchParams({
                    tab: 'documents',
                    id: file._id,
                }).toString(),
            },
            { replace: true }
        );
        onResultClick();
    };

    return (
        <div className="floating-list-item py-[10px]" onClick={handleClick}>
            <div className="flex items-center gap-3">
                <div>
                    <DocFileIcon className="w-7" extension={getFileExtension(file.path)} />
                </div>
                <OverflowTooltip className="text-sm font-medium text-grayscale-600">
                    {/* <span className="font-semibold">{file.category as string}</span>
                    <span> - </span> */}
                    <span>{file.name}</span>
                </OverflowTooltip>
            </div>
        </div>
    );
};

/******************************************* ExternalLinkSearchResultRow *******************************************/
interface ExternalLinkSearchResultRowProps {
    data: IDocumentFileModel;
    onResultClick: () => void;
}
const ExternalLinksSearchResultRow: FC<ExternalLinkSearchResultRowProps> = (props) => {
    const { data: externalLink, onResultClick } = props;

    const navigate = useNavigate();

    const handleClick = () => {
        navigate(
            {
                pathname: `/documents`,
                search: createSearchParams({
                    tab: 'external_link',
                    id: externalLink._id,
                }).toString(),
            },
            { replace: true }
        );
        onResultClick();
    };

    return (
        <div className="floating-list-item py-[10px]" onClick={handleClick}>
            <OverflowTooltip className="text-sm font-medium text-grayscale-600">
                {/* <span className="font-semibold">{externalLink.category as string}</span>
                <span> - </span> */}
                <span>{externalLink.name}</span>
            </OverflowTooltip>
        </div>
    );
};

/******************************************* SearchResultRow *******************************************/

interface SearchResultRowProps {
    data: ProjectModel | CameraModel | CameraCCTVModel | UserModel | IDocumentFileModel;
    type: SearchTypeEnum;
    onResultClick: () => void;
}
const SearchResultRow: FC<SearchResultRowProps> = (props) => {
    const { data, type, onResultClick } = props;

    switch (type) {
        case SearchTypeEnum.PROJECT:
            return <ProjectSearchResultRow data={data as ProjectModel} onResultClick={onResultClick} />;
        case SearchTypeEnum.CAMERA:
            return <CameraSearchResultRow data={data as CameraModel} onResultClick={onResultClick} />;
        case SearchTypeEnum.CAMERA_CCTV:
            return <CameraCctvSearchResultRow data={data as CameraCCTVModel} onResultClick={onResultClick} />;
        case SearchTypeEnum.USER:
            return <UserSearchResultRow data={data as UserModel} onResultClick={onResultClick} />;
        case SearchTypeEnum.FILES:
            return <FilesSearchResultRow data={data as IDocumentFileModel} onResultClick={onResultClick} />;
        case SearchTypeEnum.EXTERNAL_LINK:
            return <ExternalLinksSearchResultRow data={data as IDocumentFileModel} onResultClick={onResultClick} />;

        default:
            return null;
    }
};

export default SearchResultRow;
